import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardBg,
  borderPrimary,
  secondaryText,
  greenTextColor,
  lightGreenBG,
  semiBoldText,
  smallTextSize,
  rounded,
} from '../../../styling/stylingHelper';

/**
 * MobileSelect — generic, fully-controlled custom dropdown for cramped mobile UIs.
 *
 * Ported from the designer's `shared/MobileSelect.tsx`, adapted to our
 * `stylingHelper` tokens (light + dark) and our lucide icon stack. Use it where a
 * desktop popover/select is too wide on mobile. It is fully controlled (no internal
 * selection state) and closes on outside click. Options are plain strings — pass a
 * translated label list and map back to the enum in `onChange` if needed.
 *
 * (Distinct from `components/ui/DropdownSelector` which manages its own state,
 * takes `{value,label}` options and has no click-outside close / align control.)
 *
 * @example
 * const [period, setPeriod] = useState<'day' | 'week' | 'month'>('week');
 * <MobileSelect
 *   value={period}
 *   options={['day', 'week', 'month']}
 *   onChange={setPeriod}
 *   dropdownAlign="right"
 * />
 */
interface MobileSelectProps<T extends string> {
  value: T;
  options: T[];
  onChange: (v: T) => void;
  className?: string;
  dropdownAlign?: 'left' | 'right';
}

function MobileSelect<T extends string>({
  value,
  options,
  onChange,
  className,
  dropdownAlign = 'right',
}: MobileSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={cn(
          'w-full h-11 px-3 min-w-[110px] flex items-center justify-between gap-2 border whitespace-nowrap shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]',
          rounded,
          cardBg,
          borderPrimary,
          secondaryText,
          semiBoldText,
          smallTextSize,
        )}
      >
        {value}
        <ChevronDown
          className={cn(
            'w-5 h-5 transition-transform',
            secondaryText,
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            'absolute top-[calc(100%+6px)] z-50 w-full min-w-[140px] overflow-hidden border shadow-lg',
            dropdownAlign === 'left' ? 'left-0' : 'right-0',
            rounded,
            cardBg,
            borderPrimary,
          )}
        >
          {options.map((opt) => {
            const isSelected = opt === value;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={cn(
                  // option rows use a slightly larger 15px label than the trigger's 14px
                  'w-full text-left px-4 py-3 flex items-center gap-2 transition text-[15px] font-medium',
                  isSelected
                    ? cn(lightGreenBG, greenTextColor)
                    : cn(secondaryText, 'hover:bg-gray-50 dark:hover:bg-white/5'),
                )}
              >
                {isSelected ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="w-4" />
                )}
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MobileSelect;
